import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ExternalLink } from 'lucide-react'; // Naya icon website button ke liye

// --- Course Data ---
const courseData: { [key: string]: string[] } = {
  "Web Development": ["Java Full Stack", "MERN Full Stack", "Python Full Stack", "ReactJS Development", "Backend Development", "Frontend Development"],
  "App Development": ["Android Development", "iOS Development", "Flutter Development"],
  "Data Science & AI": ["Data Science with Python", "Machine Learning", "Artificial Intelligence", "Power BI & Visualization"],
  "Cloud & DevOps": ["AWS Cloud", "Azure", "DevOps Engineer", "Linux Administration"],
  "Programming": ["Core Java", "C++ Programming", "Python Programming", "Spring Boot", "Data Structures (DSA)"],
  "Database": ["SQL", "MySQL", "MongoDB"],
  "Software Testing": ["Manual Testing", "Automation (Selenium)", "API Testing"]
};

const EnquiryPage: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Form State
  const [formData, setFormData] = useState({
    name: "", email: "", mobile: "", collegeName: "",
    isWorking: "", companyName: "", experience: "",
    category: "", course: "", source: "", otherSource: ""
  });

  const [subCourses, setSubCourses] = useState<string[]>([]);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Handlers (Same as before)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrorMessage("");
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value;
    setFormData(prev => ({ ...prev, category, course: "" })); 
    setSubCourses(courseData[category] || []);
    setErrorMessage("");
  };

  const handleWorkingChange = (status: string) => {
    setFormData(prev => ({
      ...prev,
      isWorking: status,
      companyName: status === "No" ? "" : prev.companyName,
      experience: status === "No" ? "" : prev.experience
    }));
    setErrorMessage("");
  };

  // --- API Connection (Apka backend URL) ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");
    
    try {
      // Make sure your spring boot is running on 2025 and CORS is allowed
      const response = await fetch('https://www.projectinfotech.in/api/enquiry/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setErrorMessage("Something went wrong on our end. Please try again.");
      }
    } catch (error) {
      console.error("Submission Error:", error);
      setErrorMessage("Failed to connect to the server. Please check your internet connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "", email: "", mobile: "", collegeName: "",
      isWorking: "", companyName: "", experience: "",
      category: "", course: "", source: "", otherSource: ""
    });
    setSubCourses([]);
    setFocusedField(null);
    setErrorMessage("");
  };

  // CSS Classes (Same as before)
  const inputClass = "w-full bg-transparent border-b border-gray-300 py-2 focus:border-red-600 focus:border-b-2 outline-none transition-all text-gray-900 text-base placeholder-gray-400";
  const questionCardClass = "bg-white p-6 rounded-lg shadow-sm border border-gray-200 transition-all duration-300";
  const questionLabelClass = "text-[15px] font-medium text-gray-900 mb-4 block";
  const requiredAsterisk = <span className="text-red-500 ml-1">*</span>;

  const getCardClass = (fieldName: string) => {
    return `${questionCardClass} ${focusedField === fieldName ? 'border-l-4 border-l-red-600 shadow-md' : ''}`;
  };

  // --- NEW PROFESSIONAL SUCCESS VIEW ---
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f0ebf8] flex flex-col items-center justify-center p-4 font-sans selection:bg-green-100">
        <Helmet>
          <title>Enquiry Submitted | PIT</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="max-w-md w-full bg-white p-10 rounded-[2rem] shadow-xl text-center border border-white"
        >
          {/* Animated Tick Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.1 }}
            className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-8"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-green-500">
              <motion.polyline 
                points="20 6 9 17 4 12"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              />
            </svg>
          </motion.div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4">Application Received!</h2>
          <p className="text-gray-600 mb-10 leading-relaxed">
            Thank you for choosing PIT. Our academic counselor will review your profile and connect with you shortly.
          </p>

          {/* Primary Action: Visit Website */}
          <a 
            href="https://www.projectinfotech.in" 
            className="group flex items-center justify-center gap-3 w-full bg-red-600 text-white font-bold py-4 rounded-xl hover:bg-red-700 transition-all transform hover:scale-[1.02] shadow-lg hover:shadow-red-200 mb-4"
          >
            Visit Official Website 
            <ExternalLink size={18} className="group-hover:translate-x-1 transition-transform"/>
          </a>

          {/* Secondary Action: Submit Another */}
          <button 
            onClick={() => { setSubmitted(false); resetForm(); }} 
            className="text-gray-500 font-medium text-sm hover:text-red-600 hover:bg-gray-50 px-4 py-2 rounded-lg transition-colors"
          >
            Submit another response
          </button>
        </motion.div>
      </div>
    );
  }

  // --- Form View (Same as before) ---
  return (
    <div className="min-h-screen bg-[#f0ebf8] py-8 px-4 font-sans selection:bg-red-200">
      <Helmet>
        <title>Career Enquiry Form | PIT Institute</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="max-w-[640px] mx-auto space-y-4">
        
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Header Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 border-t-8 border-t-red-600 p-8">
            <h1 className="text-[32px] font-normal text-gray-900 mb-3 leading-tight">PIT Career Enquiry Form</h1>
            <p className="text-gray-600 text-sm mb-4">Please fill out the details below so we can understand your background and guide you towards the perfect program.</p>
            <hr className="border-gray-200 mb-4" />
            <p className="text-red-500 text-xs font-medium">* Indicates required question</p>
            
            {errorMessage && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm font-medium rounded">
                {errorMessage}
              </div>
            )}
          </div>

          {/* Form Fields (Same as before) */}
          <div className={getCardClass('name')} onClick={() => setFocusedField('name')}>
            <label className={questionLabelClass}>Full Name {requiredAsterisk}</label>
            <input required type="text" name="name" value={formData.name} onChange={handleChange} className={inputClass} placeholder="Your answer" />
          </div>
          <div className={getCardClass('email')} onClick={() => setFocusedField('email')}>
            <label className={questionLabelClass}>Email Address {requiredAsterisk}</label>
            <input required type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} placeholder="Your answer" />
          </div>
          <div className={getCardClass('mobile')} onClick={() => setFocusedField('mobile')}>
            <label className={questionLabelClass}>WhatsApp Number {requiredAsterisk}</label>
            <input required type="tel" name="mobile" value={formData.mobile} onChange={handleChange} className={inputClass} placeholder="Your answer" />
          </div>
          <div className={getCardClass('college')} onClick={() => setFocusedField('college')}>
            <label className={questionLabelClass}>College / University Name {requiredAsterisk}</label>
            <input required type="text" name="collegeName" value={formData.collegeName} onChange={handleChange} className={inputClass} placeholder="Your answer" />
          </div>
          <div className={getCardClass('working')} onClick={() => setFocusedField('working')}>
            <label className={questionLabelClass}>Are you currently working? {requiredAsterisk}</label>
            <div className="space-y-4 mt-2">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.isWorking === 'Yes' ? 'border-red-600' : 'border-gray-400 group-hover:border-gray-500'}`}>
                  {formData.isWorking === 'Yes' && <div className="w-2.5 h-2.5 bg-red-600 rounded-full"></div>}
                </div>
                <span className="text-gray-800 text-sm">Yes, I am working</span>
                <input required type="radio" name="isWorking" value="Yes" className="hidden" onChange={() => handleWorkingChange('Yes')} />
              </label>
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.isWorking === 'No' ? 'border-red-600' : 'border-gray-400 group-hover:border-gray-500'}`}>
                  {formData.isWorking === 'No' && <div className="w-2.5 h-2.5 bg-red-600 rounded-full"></div>}
                </div>
                <span className="text-gray-800 text-sm">No, I am a student / fresher</span>
                <input required type="radio" name="isWorking" value="No" className="hidden" onChange={() => handleWorkingChange('No')} />
              </label>
            </div>
          </div>
          <AnimatePresence>
            {formData.isWorking === "Yes" && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-4 overflow-hidden">
                <div className={getCardClass('company')} onClick={() => setFocusedField('company')}>
                  <label className={questionLabelClass}>Company Name {requiredAsterisk}</label>
                  <input required type="text" name="companyName" value={formData.companyName} onChange={handleChange} className={inputClass} placeholder="Your answer" />
                </div>
                <div className={getCardClass('experience')} onClick={() => setFocusedField('experience')}>
                  <label className={questionLabelClass}>Total Experience (in years) {requiredAsterisk}</label>
                  <input required type="number" step="0.5" name="experience" value={formData.experience} onChange={handleChange} className={inputClass} placeholder="Your answer" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className={getCardClass('category')} onClick={() => setFocusedField('category')}>
            <label className={questionLabelClass}>Select Domain / Category {requiredAsterisk}</label>
            <select required name="category" value={formData.category} onChange={handleCategoryChange} className="w-full max-w-xs border border-gray-300 rounded p-3 text-sm text-gray-800 focus:border-red-600 outline-none bg-white cursor-pointer">
              <option value="" disabled>Choose</option>
              {Object.keys(courseData).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <AnimatePresence>
            {formData.category && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden pt-1">
                <div className={getCardClass('course')} onClick={() => setFocusedField('course')}>
                  <label className={questionLabelClass}>Select Specific Course {requiredAsterisk}</label>
                  <select required name="course" value={formData.course} onChange={handleChange} className="w-full max-w-xs border border-gray-300 rounded p-3 text-sm text-gray-800 focus:border-red-600 outline-none bg-white cursor-pointer">
                    <option value="" disabled>Choose</option>
                    {subCourses.map(course => (
                      <option key={course} value={course}>{course}</option>
                    ))}
                  </select>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className={getCardClass('source')} onClick={() => setFocusedField('source')}>
            <label className={questionLabelClass}>How did you hear about us? {requiredAsterisk}</label>
            <div className="space-y-4 mt-2">
              {["Google Search", "LinkedIn", "Instagram / Facebook", "Friend / Referral", "Other"].map((srcOption) => (
                <label key={srcOption} className="flex items-center gap-3 cursor-pointer group">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.source === srcOption ? 'border-red-600' : 'border-gray-400 group-hover:border-gray-500'}`}>
                    {formData.source === srcOption && <div className="w-2.5 h-2.5 bg-red-600 rounded-full"></div>}
                  </div>
                  <span className="text-gray-800 text-sm">{srcOption}</span>
                  <input required type="radio" name="source" value={srcOption} className="hidden" onChange={handleChange} />
                </label>
              ))}
            </div>
            <AnimatePresence>
              {formData.source === "Other" && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden mt-4">
                   <input required type="text" name="otherSource" value={formData.otherSource} onChange={handleChange} className={inputClass} placeholder="Please specify" autoFocus/>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Submit Actions */}
          <div className="flex items-center justify-between pt-4 pb-10">
            <button 
              disabled={isSubmitting} 
              type="submit" 
              className="bg-red-600 text-white px-6 py-2.5 rounded font-medium text-[14px] hover:bg-red-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
            <button type="button" onClick={resetForm} className="text-xs text-gray-500 font-medium hover:bg-gray-200 px-2 py-1 rounded transition-colors">
              Clear form
            </button>
          </div>
        </form>
        <div className="text-center pb-10 text-xs text-gray-500">
          Never submit passwords through Google Forms.
        </div>
      </div>
    </div>
  );
};

export default EnquiryPage;